import { Injectable, inject } from '@angular/core';
import { RateApplicationModalComponent } from '@app/shared/components/rate-application-modal/rate-application-modal.component';
import { RateWeekModalComponent } from '@app/shared/components/rate-week-modal/rate-week-modal.component';
import { App } from '@capacitor/app';
import { ModalController } from '@ionic/angular';
import { Subject, takeUntil, Observable, filter, switchMap, take } from 'rxjs';
import { FeedbackService } from '../services/feedback.service';
import { Store } from '@ngrx/store';
import { CoreState } from '../store/core.slice';
import { ApplicationRating, FeedbackRequired } from '../types/feedback/feedback.types';
import { BaseSandbox } from '../store/base.sandbox';
import {
  checkFeedback,
  checkFeedbackState,
  sendApplicationFeedback,
  sendApplicationFeedbackState,
  sendWeekFeedback,
  sendWeekFeedbackState,
} from '../store/feedback.slice';
import { PluginListenerHandle } from '@capacitor/core';
import { awaitSuccessOrCancel } from '@app/shared/utils/pipe.functions';
import { WindowLocationService } from '../services/window-location.service';
import { AnalyticsService } from '../services/analytics.service';
import { AnalyticsEvents } from '../enums/analytics-events.enum';
import { ProcessNames } from '../enums/process.enum';

@Injectable({ providedIn: 'root' })
export class FeedbackSandbox extends BaseSandbox {
  private analyticsService = inject(AnalyticsService);

  private appStateChangeListener: PluginListenerHandle;
  private stopListeningToFeedback$ = new Subject<void>();
  private isWeekFeedbackModalOpen = false;
  private isApplicationFeedbackOpen = false;

  checkFeedback$: Observable<FeedbackRequired> = this.store.select(checkFeedbackState.data);
  checkFeedbackLoading$: Observable<boolean> = this.store.select(checkFeedbackState.loading);
  sentApplicationFeedback$: Observable<boolean> = this.store.select(sendApplicationFeedbackState.loaded);
  sendingApplicationFeedback$: Observable<boolean> = this.store.select(sendApplicationFeedbackState.loading);
  sentWeekFeedback$: Observable<boolean> = this.store.select(sendWeekFeedbackState.loaded);
  sendingWeekFeedback$: Observable<boolean> = this.store.select(sendWeekFeedbackState.loading);

  constructor(
    private modalController: ModalController,
    private feedbackService: FeedbackService,
    private windowLocationService: WindowLocationService,
    store: Store<CoreState>
  ) {
    super(store);
  }

  async initFeedbackNeededListener(feedbackWeekFeatureEnabled: boolean, feedbackAppFeatureEnabled: boolean): Promise<void> {
    this.feedbackNeededCheck();

    this.checkFeedback$.pipe(takeUntil(this.stopListeningToFeedback$)).subscribe((feedback) => {
      if (feedback?.rateYourWeek && feedbackWeekFeatureEnabled) {
        this.openWeekFeedbackModal();
      }
      if (feedback?.application && feedbackAppFeatureEnabled) {
        this.openApplicationFeedbackModal();
      }
    });
  }

  async setAppStateChangeListener(): Promise<void> {
    this.appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        this.feedbackNeededCheck();
      }
    });
  }

  async openWeekFeedbackModal(): Promise<void> {
    if (this.isWeekFeedbackModalOpen) {
      return;
    }
    this.isWeekFeedbackModalOpen = true;
    await this.waitUntilNoModalIsOpen();
    const modal = await this.modalController.create({
      component: RateWeekModalComponent,
      componentProps: {
        close: () => modal.dismiss(),
        submit: (code: number) => this.sendWeekFeedback(code),
        submitting$: this.sendingWeekFeedback$,
        submitted$: this.sendingWeekFeedback$.pipe(awaitSuccessOrCancel(() => this.sentWeekFeedback$)),
      },
      cssClass: 'rate-week-modal',
    });
    await modal.present();
    await modal.onDidDismiss();
    this.isWeekFeedbackModalOpen = false;
  }

  openWeekFeedbackModalForNotification(): void {
    this.feedbackNeededCheck();
    this.checkFeedbackLoading$
      .pipe(
        filter((loading) => !loading),
        switchMap(() => this.checkFeedback$),
        take(1)
      )
      .subscribe((feedbackRequired) => {
        if (feedbackRequired?.rateYourWeek) {
          this.openWeekFeedbackModal();
        }
      });
  }

  private async openApplicationFeedbackModal(): Promise<void> {
    if (this.isApplicationFeedbackOpen) {
      return;
    }
    this.isApplicationFeedbackOpen = true;
    await this.waitUntilNoModalIsOpen();
    const modal = await this.modalController.create({
      component: RateApplicationModalComponent,
      componentProps: {
        close: () => modal.dismiss(),
        submit: (rating: ApplicationRating, feedbackText?: string) =>
          this.sendApplicationFeedback(rating, feedbackText),
        openAppStore: async () => {
          await modal.dismiss();
          this.windowLocationService.openStore();
        },
        submitting$: this.sendingApplicationFeedback$,
        submitted$: this.sendingApplicationFeedback$.pipe(awaitSuccessOrCancel(() => this.sentApplicationFeedback$)),
      },
      cssClass: ['auto-height-modal'],
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      handle: false,
    });
    await modal.present();
    await modal.onDidDismiss();
    this.isApplicationFeedbackOpen = false;
  }

  /** Feedback never opens on top of another modal (e.g. the dashboard celebration); it waits for it to close. */
  private async waitUntilNoModalIsOpen(): Promise<void> {
    let topModal = await this.modalController.getTop();
    while (topModal) {
      await topModal.onDidDismiss();
      topModal = await this.modalController.getTop();
    }
  }

  private feedbackNeededCheck(): void {
    this.load(checkFeedback, {}, this.feedbackService.checkFeedback());
  }

  private sendApplicationFeedback(rating: ApplicationRating, feedback?: string): void {
    this.load(
      sendApplicationFeedback,
      { rating, feedback },
      this.feedbackService.sendApplicationFeedback(rating, feedback)
    );
  }

  private sendWeekFeedback(code: number): void {
    this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
      name: ProcessNames.RateYourWeek,
    });
    this.load(sendWeekFeedback, { code }, this.feedbackService.sendWeekFeedback(code));
  }

  removeAppStateChangeListener(): void {
    this.stopListeningToFeedback$.next();
    if (this.appStateChangeListener) {
      this.appStateChangeListener.remove();
      this.appStateChangeListener = null;
    }
  }
}
